/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import {
  GetAttributesFormResponse,
  ResponseAttributesFormItemsProps,
} from '@/account/types';
import { AttributesFormModel, FieldEnumList } from '../models';
import { convertToCamelCase } from '@/account/lib/convertCase';

export const cloneArrayIfExists = (
  fields: ResponseAttributesFormItemsProps[]
) => {
  let multilineItems: any = [];

  for (const element of fields) {
    if (element.frontend_input !== 'MULTILINE' || element.multiline_count < 2) {
      continue;
    }

    for (let i = 2; i <= element.multiline_count; i++) {
      const newItem = {
        ...element,
        is_required: false,
        name: `${element.code}_multiline_${i}`,
        code: `${element.code}_multiline_${i}`,
        id: `${element.code}_multiline_${i}`,
      };

      multilineItems.push(newItem);
    }
  }

  return multilineItems;
};

const transformCustomUpperCode = (code: string) => {
  switch (code) {
    case 'middlename':
      return 'middleName';
    case 'firstname':
      return 'firstName';
    case 'lastname':
      return 'lastName';
    default:
      return convertToCamelCase(code);
  }
};

const transformOptions = (item: any) => {
  if (!item?.options) return [];

  return item?.options?.map((el: any) => {
    return {
      isDefault: el?.is_default ?? false,
      text: el?.label ?? '',
      value: el?.value ?? '',
    };
  });
};

export const transformAttributesForm = (
  response: GetAttributesFormResponse
): AttributesFormModel[] => {
  const items = response?.data?.attributesForm?.items || [];

  if (!items.length) return [];

  const fields = items
    .filter((el) => !el.frontend_input?.includes('HIDDEN'))
    ?.map(({ code, ...other }) => {
      const isDefaultCode = code !== 'country_id' ? code : 'country_code';

      return {
        ...other,
        name: isDefaultCode,
        id: isDefaultCode,
        code: isDefaultCode,
      };
    });

  const multilineItems = cloneArrayIfExists(fields as any);

  const attributesConfig = fields
    .concat(multilineItems)
    .map((item) => {
      return {
        code: item?.code,
        name: item?.name,
        id: item?.id,
        label: item?.label ?? '',
        entityType: item?.entity_type,
        className: item?.frontend_class ?? '',
        defaultValue: item?.default_value ?? '',
        fieldType: item?.frontend_input as FieldEnumList,
        multilineCount: item?.multiline_count ?? 0,
        orderNumber: Number(item?.sort_order) || 0,
        isHidden: false,
        isUnique: item?.is_unique ?? false,
        required: item?.is_required ?? false,
        validateRules: item?.validate_rules ?? [],
        options: transformOptions(item),
        customUpperCode: transformCustomUpperCode(item?.code),
      };
    })
    .sort((a, b) => Number(a.orderNumber) - Number(b.orderNumber));

  return attributesConfig;
};
